/**
 * SBG Funding 2026 design tokens for TypeScript.
 *
 * The values themselves live in `sbg-tokens.css` (generated from
 * docs/brand/tokens.json); everything here is a `var(--sbg-…)` reference, so
 * a style written with these tokens follows the light and dark themes with
 * no extra work. The brand book is docs/brand (start at README.md).
 *
 * Use role tokens (`sbg.text.primary`, `sbg.bg.surface`, `sbg.status.success.text`)
 * in components. Primitives (`sbg.palette.blue[500]`) are for the rare case
 * no role fits. Never write hex literals in components.
 */

const v = (name: string) => `var(--sbg-${name})`;

const statusRole = (name: 'info' | 'success' | 'warning' | 'danger') => ({
  bg: v(`${name}-bg`),
  border: v(`${name}-border`),
  icon: v(`${name}-icon`),
  text: v(`${name}-text`),
});

const ramp = <K extends string | number>(name: string, steps: readonly K[]) =>
  Object.fromEntries(steps.map((step) => [step, v(`${name}-${step}`)])) as Record<K, string>;

export const sbg = {
  bg: {
    canvas: v('bg-canvas'),
    surface: v('bg-surface'),
    surfaceSubtle: v('bg-surface-subtle'),
    raised: v('bg-raised'),
    hover: v('bg-hover'),
    pressed: v('bg-pressed'),
    selected: v('bg-selected'),
    selectedStrong: v('bg-selected-strong'),
    unread: v('bg-unread'),
    inverse: v('bg-inverse'),
    brand: v('bg-brand'),
    brandDeep: v('bg-brand-deep'),
    overlay: v('bg-overlay'),
    skeleton: v('bg-skeleton'),
    skeletonShine: v('bg-skeleton-shine'),
    disabled: v('bg-disabled'),
    track: v('bg-track'),
    switchOff: v('bg-switch-off'),
    avatar: v('bg-avatar'),
    tile: v('bg-tile'),
    emptyMark: v('bg-empty-mark'),
    emptySpark: v('bg-empty-spark'),
  },
  text: {
    primary: v('text-primary'),
    secondary: v('text-secondary'),
    /** Metadata on white surfaces only; fails AA on the Cloud canvas. */
    tertiary: v('text-tertiary'),
    disabled: v('text-disabled'),
    /** Blue text: active tabs, eyebrows, selected values (500 fills, 600 writes). */
    accent: v('text-accent'),
    link: v('text-link'),
    linkHover: v('text-link-hover'),
    inverse: v('text-inverse'),
    onBrand: v('text-on-brand'),
    onVolt: v('text-on-volt'),
    avatar: v('text-avatar'),
  },
  icon: {
    tile: v('icon-tile'),
  },
  border: {
    subtle: v('border-subtle'),
    default: v('border-default'),
    strong: v('border-strong'),
    hover: v('border-hover'),
    selected: v('border-selected'),
    onBrand: v('border-on-brand'),
  },
  focus: {
    ring: v('focus-ring'),
    ringSoft: v('focus-ring-soft'),
  },
  action: {
    primary: v('action-primary'),
    primaryHover: v('action-primary-hover'),
    primaryActive: v('action-primary-active'),
    volt: v('action-volt'),
    voltHover: v('action-volt-hover'),
    voltActive: v('action-volt-active'),
    voltBorder: v('action-volt-border'),
    secondaryBorder: v('action-secondary-border'),
    secondaryHover: v('action-secondary-hover'),
    danger: v('action-danger'),
    dangerHover: v('action-danger-hover'),
  },
  status: {
    info: statusRole('info'),
    success: statusRole('success'),
    warning: statusRole('warning'),
    danger: statusRole('danger'),
    neutral: { bg: v('neutral-bg'), border: v('neutral-border'), icon: v('neutral-text'), text: v('neutral-text') },
    /** Volt: money moved. The only status that wears Volt. */
    funded: { bg: v('funded-bg'), border: v('funded-border'), icon: v('funded-text'), text: v('funded-text') },
  },
  chart: {
    surface: v('chart-surface'),
    grid: v('chart-grid'),
    axis: v('chart-axis'),
    label: v('chart-label'),
    /** Categorical series, assigned in order by entity (book §13.3). */
    series: [1, 2, 3, 4, 5, 6, 7, 8].map((n) => v(`chart-${n}`)),
    /** Sequential (magnitude), light to dark. */
    seq: [1, 2, 3, 4, 5, 6, 7, 8, 9].map((n) => v(`chart-seq-${n}`)),
    /** Diverging: danger ← neutral midpoint (4) → blue. */
    div: [1, 2, 3, 4, 5, 6, 7].map((n) => v(`chart-div-${n}`)),
  },
  palette: {
    blue: ramp('blue', [25, 50, 100, 200, 300, 400, 500, 600, 700, 800, 850, 900, 950] as const),
    slate: ramp('slate', [0, 25, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950] as const),
    volt: ramp('volt', [50, 100, 200, 300, 500, 600, 700, 900] as const),
    cyan: ramp('cyan', [50, 300, 500, 700] as const),
    success: ramp('success', [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const),
    warning: ramp('warning', [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const),
    danger: ramp('danger', [50, 100, 200, 300, 400, 500, 600, 700, 800, 900] as const),
  },
  radius: {
    xs: v('radius-xs'),
    sm: v('radius-sm'),
    md: v('radius-md'),
    lg: v('radius-lg'),
    xl: v('radius-xl'),
    '2xl': v('radius-2xl'),
    pill: v('radius-pill'),
    /** Inputs, selects, menu rows (8px). */
    control: v('radius-control'),
    /** App cards (16px). */
    card: v('radius-card'),
    /** Dialogs, sheets, featured cards (20px). */
    panel: v('radius-panel'),
    /** Buttons, chips, nav items, tags (pill). */
    action: v('radius-action'),
  },
  shadow: {
    xs: v('shadow-xs'),
    sm: v('shadow-sm'),
    md: v('shadow-md'),
    lg: v('shadow-lg'),
  },
  duration: {
    instant: v('duration-instant'),
    fast: v('duration-fast'),
    base: v('duration-base'),
    slow: v('duration-slow'),
  },
  ease: {
    standard: v('ease-standard'),
    enter: v('ease-enter'),
    exit: v('ease-exit'),
  },
  control: {
    xs: v('control-xs'),
    sm: v('control-sm'),
    md: v('control-md'),
    lg: v('control-lg'),
    xl: v('control-xl'),
  },
  layout: {
    sidebarW: v('sidebar-w'),
    sidebarWCollapsed: v('sidebar-w-collapsed'),
    topbarH: v('topbar-h'),
    contentNarrow: v('content-narrow'),
    contentReading: v('content-reading'),
    contentDefault: v('content-default'),
    contentWide: v('content-wide'),
  },
  z: {
    raised: v('z-raised'),
    sticky: v('z-sticky'),
    header: v('z-header'),
  },
  font: {
    display: v('font-display'),
    body: v('font-body'),
    mono: v('font-mono'),
  },
  gradient: {
    sky: v('gradient-sky'),
    lift: v('gradient-lift'),
    horizon: v('gradient-horizon'),
    night: v('gradient-night'),
    fade: v('gradient-fade'),
  },
  glass: {
    bg: v('glass-bg'),
    border: v('glass-border'),
    blur: v('glass-blur'),
  },
} as const;

export type SbgTypeStyle =
  | 'display-xl' | 'display-lg'
  | 'heading-1' | 'heading-2' | 'heading-3' | 'heading-4'
  | 'numeric-xl' | 'numeric-lg' | 'numeric-md'
  | 'body-lg' | 'body-md' | 'body-mobile' | 'body-sm' | 'body-xs'
  | 'label-md' | 'label-sm' | 'label-xs'
  | 'caption' | 'overline' | 'mono-sm';

/**
 * A brand type style as an `sx` fragment: `sx={{ ...typeStyle('heading-3') }}`.
 * Prefer the matching Typography variant when rendering text.
 */
export const typeStyle = (name: SbgTypeStyle) => ({
  font: `var(--sbg-type-${name})`,
  letterSpacing: `var(--sbg-type-${name}-tracking)`,
  ...(name === 'overline' ? { textTransform: 'uppercase' as const } : null),
});

/** A theme-aware translucent wash of a token: `mix(sbg.action.primary, 12)`. */
export const mix = (color: string, percent: number, base = 'transparent') =>
  `color-mix(in srgb, ${color} ${percent}%, ${base})`;

/** The brand focus outline (2px ring, 2px offset). */
export const focusRing = {
  outline: `2px solid ${sbg.focus.ring}`,
  outlineOffset: '2px',
} as const;

/** One standard hover/color transition. */
export const transition = (...properties: string[]) =>
  properties.map((p) => `${p} ${sbg.duration.fast} ${sbg.ease.standard}`).join(', ');

export type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'funded';

export const statusColors = (tone: StatusTone) => sbg.status[tone];

/**
 * Resolves a token to its current computed value, for the few places that
 * cannot take a CSS variable (canvas drawing, generated images). Re-read it
 * after the theme changes.
 */
export const readToken = (name: string, fallback = '') => {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(`--sbg-${name}`).trim();
  return value || fallback;
};

/**
 * Raw 2026 hex values (light theme) for code that needs a literal color,
 * such as email templates or SVG exports. Components use `sbg` instead.
 */
export const SBG_HEX = {
  navy: '#002B60',
  blue: '#006BF1',
  blue600: '#0056C1',
  blue700: '#004091',
  blue50: '#E5F0FE',
  blue100: '#CCE2FD',
  volt: '#F5FF33',
  voltBorder: '#B2BB02',
  spark: '#00C3FF',
  cloud: '#EEF2F9',
  white: '#FFFFFF',
  slate25: '#F6F8FC',
  slate100: '#DCE3EE',
  slate200: '#C4CEDE',
  slate400: '#7C89A6',
  slate500: '#616F91',
  slate600: '#4B5A7C',
  slate700: '#344465',
  successIcon: '#17843F',
  successText: '#176933',
  successBg: '#E9FDEC',
  warningFill: '#FFA100',
  warningText: '#895203',
  warningBg: '#FEF7F0',
  dangerFill: '#CC0000',
  dangerText: '#9C1B1B',
  dangerBg: '#FFF2F0',
} as const;
